/*----------------------------------
- HOME: DEMO BROWSE STRIP
----------------------------------*/

/**
 * The strip under the "Find a name" rows (`.bw-stats`, the mock's, mock parity web-frame-12 and Gaetan's default 6):
 * the search's names with its open and aftermarket shares, then its median ask. Every figure is served for the bare
 * `/domains` search (`readHomeDemoStripFigures`); a catalog-wide search's figures are page-sample estimates and carry
 * the estimate mark, as on `/domains`. The mock's register-from price, under-value count, median upside and top words
 * are not drawn: they would be new public aggregates (ADR-0028).
 *
 * @docs docs/features/website-landing-experience
 * @adr  ADR-0028
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import type { DatabaseBrowseCountPrecision } from '@uniquedomains/components/contracts/domains/browseFacets';
import { BROWSE_ESTIMATE_SPOKEN_LABEL, BROWSE_ESTIMATE_SYMBOL } from '@uniquedomains/components/radar/catalogs/browseResultCountCopy';

import { HOME_BROWSE_COPY } from '@/client/catalogs/landing/home/homeCopy';
import { formatCompactStat } from '@/client/pages/landing/lib/formatCompactStat';
import type { HomeDemoStripFigures, HomeDemoStripSegment } from '@/client/pages/landing/lib/homeDemoStrip';

/*----------------------------------
- TYPES
----------------------------------*/

type HomeBrowseStripProps = {
    figures: HomeDemoStripFigures;
    countPrecision: DatabaseBrowseCountPrecision;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

const COPY = HOME_BROWSE_COPY.strip;

/** The median ask written out in whole dollars, as the mock's "$1,950" (the count stays compact, "32.2M"). */
const WHOLE_USD = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const formatWholeUsd = (value: number): string => WHOLE_USD.format(value);

/** The mock's four status swatches before the count, each sized from its served share (the mock's tones in its order). */
const SEGMENT_TONES: Record<HomeDemoStripSegment['id'], string> = {
    open: 'var(--ud-positive)',
    aftermarket: 'var(--ud-viz-2)',
    premium: 'var(--ud-alert)',
    other: 'var(--ud-line-hover)',
};

/** The bar's drawn width in px (the mock's four 10px swatches and their gaps). */
const BAR_WIDTH = 46;

/*----------------------------------
- COMPONENT
----------------------------------*/

const HomeBrowseStrip = ({ figures, countPrecision }: HomeBrowseStripProps) => {
    const estimate = countPrecision === 'sampled' ? BROWSE_ESTIMATE_SYMBOL : '';
    const spoken = countPrecision === 'sampled' ? <span class="ud-sr-only"> {BROWSE_ESTIMATE_SPOKEN_LABEL}</span> : null;
    // The shares come from the same page sample as the count on a catalog-wide search, so they carry its mark too.
    const shares = [
        figures.openPct === null ? null : `${estimate}${COPY.open(figures.openPct)}`,
        figures.aftermarketPct === null ? null : `${estimate}${COPY.aftermarket(figures.aftermarketPct)}`,
    ].filter((part): part is string => part !== null);
    if (figures.count === null && figures.medianAsk === null) return null;

    return (
        <div class="bw-stats" role="group" aria-label={COPY.label} data-testid="home-demo-strip">
            {figures.count === null ? null : (
                <div class="bs">
                    <b>
                        {figures.bar.length ? (
                            <span class="seg4" aria-hidden="true">
                                {figures.bar.map((segment) => (
                                    <i key={segment.id} style={`width:${Math.round((segment.pct / 100) * BAR_WIDTH)}px;background:${SEGMENT_TONES[segment.id]}`} />
                                ))}
                            </span>
                        ) : null}
                        <span class="cnt">
                            {`${estimate}${formatCompactStat(figures.count)}`}
                            {spoken}
                        </span>
                        <small>{HOME_BROWSE_COPY.namesUnit}</small>
                    </b>
                    {shares.length ? <span>{shares.join(' · ')}</span> : null}
                </div>
            )}
            {figures.medianAsk === null ? null : (
                <div class="bs">
                    <b>
                        {`${estimate}$${formatWholeUsd(figures.medianAsk)}`}
                        {spoken}
                    </b>
                    <span>{COPY.medianAsk}</span>
                </div>
            )}
        </div>
    );
};

export default HomeBrowseStrip;
